import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { participantRoute } from '@/features/dashboard/participants/routes/participantRoute.ts';
import { formatDestination } from '@/features/dashboard/tours/types/DestinationDto.utils.ts';
import { DetailsMode } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { participantTabLinks } from '@/features/dashboard/bookingChanges/routes/detailsTabs.ts';
import { initialsOf } from '@/utils/personName.ts';
import { toChangePackageStatus } from '@/features/dashboard/bookingChanges/types/BookingChangeDetailDto.mapper.ts';
import { DetailsProvider } from '@/features/dashboard/bookingChanges/contexts/DetailsContext.ts';
import useChangeDraft from '@/features/dashboard/bookingChanges/hooks/useChangeDraft.ts';
import useGetBookingSummary from '@/features/dashboard/bookingChanges/hooks/useGetBookingSummary.ts';
import useParticipantBillView from '@/features/dashboard/bookingChanges/hooks/useParticipantBillView.ts';
import useBookingBillView from '@/features/dashboard/bookingChanges/hooks/useBookingBillView.ts';
import BookingActionsMenu from '@/features/dashboard/bookingChanges/components/bookingActions/BookingActionsMenu.tsx';
import WithdrawalModal from '@/features/dashboard/bookingChanges/components/withdrawal/WithdrawalModal.tsx';
import Chip from '@/components/ui/Chip.tsx';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import {
  getParticipantStatusLabel,
  PARTICIPANT_STATUS_TONE,
} from '@/features/dashboard/participants/types/ParticipantStatus.utils.ts';
import { getParticipantDetailsQueryOptions } from '@/features/dashboard/participants/hooks/useGetParticipantDetails.ts';
import { getBookingStatusLabel } from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import { bookingStatusTone } from '@/features/dashboard/bookingChanges/components/bookingDetails/bookingHeaderPresentation.ts';
import TagPickerRow from '@/features/dashboard/tags/components/TagPickerRow.tsx';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import {
  DetailsHeader,
  DetailsLayout,
  DetailsMetaItem,
  useIsTabMenu,
} from '@/components/details';
import { ChangeDraftBanner } from '@/features/dashboard/bookingChanges/components/layout';
import {
  EditFieldModalHost,
  EditModeToggle,
  ExitEditingModal,
  useEditField,
  useExitEditing,
} from '@/features/dashboard/bookingChanges/components/editing';
import { DetailsSidebar } from '@/features/dashboard/bookingChanges/components/sidebar';
import TotalBar from '@/components/ui/TotalBar.tsx';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import {
  pluralizeItems,
  pluralizeYears,
} from '@/features/dashboard/bookingChanges/utils/plurals.ts';

/**
 * Szkielet ekranu uczestnika (A1/A2): tożsamość osoby, panel boczny, belka
 * wartości zmian i modale. Treść zakładki wstawia router przez `<Outlet />` —
 * „Informacje” i „Rezerwacja” to osobne route'y ze ścieżką.
 *
 * Zakładka żyje w ścieżce, tryb w `?mode=`, więc odświeżenie strony i link
 * wysłany współpracownikowi trafiają w to samo miejsce.
 */
const ParticipantDetailsPage = () => {
  const { participantId } = participantRoute.useParams();
  const { participant, tour, bookingId, billParticipantId } =
    participantRoute.useLoaderData();
  // Dane loadera nie odświeżą się po rezygnacji — zapytanie tak, bo rezygnacja unieważnia `participants`.
  const { data: participantDetails = participant } = useQuery({
    ...getParticipantDetailsQueryOptions(participantId),
  });
  const participantWithdrawn =
    participantDetails.status === ParticipantStatus.WITHDRAWN;
  const bookingCancelled =
    participantDetails.bookingStatus === BookingStatus.CANCELLED;
  const search = participantRoute.useSearch();
  const navigate = useNavigate();
  const [onlyChanged, setOnlyChanged] = useState(false);

  const { view } = useParticipantBillView(bookingId, billParticipantId);
  // Okno rezygnacji pokazuje całą obsadę rezerwacji, żeby dało się zaznaczyć
  // rodzeństwo naraz; rachunek jest już w pamięci podręcznej po `view`.
  const { view: bookingView } = useBookingBillView(bookingId);
  const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);
  const { draft, change } = useChangeDraft(bookingId);

  /*
   * Tryb edycji wymaga szkicu, więc nie wystarczy `?mode=edit` w adresie:
   * pod ten adres da się wejść linkiem, odświeżeniem strony albo cofnięciem
   * po odrzuceniu paczki. Ekran wyglądał wtedy na gotowy do edycji, a każdy
   * zapis pozycji cicho przepadał, bo nie miał do czego trafić.
   *
   * Warunek na szkicu zamiast przekierowania jest odporny na wyścig: między
   * założeniem paczki a jej pojawieniem się w zapytaniu ekran po prostu
   * jeszcze nie pokazuje edycji, zamiast odbijać się do podglądu.
   */
  const isEditing =
    search.mode === DetailsMode.EDIT && draft !== null && !bookingCancelled;
  const summaryQuery = useGetBookingSummary(bookingId);

  /** Zmiana trybu zostaje na bieżącej zakładce — `.` to aktualna ścieżka. */
  const goToMode = (mode: DetailsMode) =>
    navigate({ to: '.', search: { mode } });

  const exitToView = () => goToMode(DetailsMode.VIEW);

  const exitEditing = useExitEditing({
    bookingId,
    draft,
    draftStatus: change ? toChangePackageStatus(change.status) : null,
    onEnterEditing: () => goToMode(DetailsMode.EDIT),
    onExitToView: exitToView,
  });

  const editField = useEditField(bookingId);

  const openBooking = () =>
    navigate({
      to: '/app/bookings/$bookingId/booking',
      params: { bookingId },
    });

  /** „Cała paczka ↗” — ten sam szkic z poziomu całej rezerwacji. */
  const openBookingInEditMode = () =>
    navigate({
      to: '/app/bookings/$bookingId/booking',
      params: { bookingId },
      search: { mode: DetailsMode.EDIT },
    });

  const draftScopeLabel = draft
    ? `${pluralizeItems(view?.draftItemCount ?? 0)} u tego uczestnika, ${draft.itemCount} w całej rezerwacji`
    : undefined;

  /**
   * Sygnatura rezerwacji prowadzi na ekran rezerwacji — ten sam skok, który
   * niżej robi „Raty i wpłaty”. Kod dojeżdża z podsumowaniem, więc do tego
   * czasu metryczka ma sam wyjazd.
   */
  const meta: DetailsMetaItem[] = [
    `${tour.tourName} · ${formatDestination(tour.destination)}`,
    ...(summaryQuery.data
      ? [
          {
            label: `Rezerwacja ${summaryQuery.data.bookingCode}`,
            to: '/app/bookings/$bookingId/booking' as const,
            params: { bookingId },
          },
        ]
      : []),
  ];

  /** Goły adres uczestnika: menu kafli na telefonie, „Informacje” szeroko. */
  const menuLink = {
    to: '/app/participants/$participantId',
    params: { participantId },
    search,
  } as const;

  /*
   * Na menu ekran ma być samym nagłówkiem i wyborem zakładki — panel boczny
   * i belka wartości zmian należą do treści, a treści tu jeszcze nie ma.
   */
  const isTabMenu = useIsTabMenu(menuLink);

  const header = (
    <DetailsHeader
      initials={initialsOf(participant.firstName, participant.lastName)}
      avatarTone="accent"
      title={`${participant.firstName} ${participant.lastName}`}
      badges={[
        // Wiek na pierwszy dzień turnusu przychodzi z kontraktu; bez daty
        // urodzenia albo daty startu backend zwraca `null` i odznaki nie ma.
        ...(participant.ageAtTourStart === null
          ? []
          : [
              <Chip
                label={pluralizeYears(participant.ageAtTourStart)}
                tone="neutral"
                fill="soft"
              />,
            ]),
        ...(participantWithdrawn
          ? [
              <StatusDot
                className="text-[15px]"
                tone={PARTICIPANT_STATUS_TONE[ParticipantStatus.WITHDRAWN]}
                label={getParticipantStatusLabel(ParticipantStatus.WITHDRAWN)}
              />,
            ]
          : []),
        ...(bookingCancelled
          ? [
              <StatusDot
                className="text-[15px]"
                tone={bookingStatusTone[BookingStatus.CANCELLED]}
                label={getBookingStatusLabel(BookingStatus.CANCELLED)}
              />,
            ]
          : []),
      ]}
      meta={meta}
      tabs={participantTabLinks(participantId, search)}
      menuLink={menuLink}
      tags={
        <TagPickerRow
          targetType={TaggableType.PARTICIPANT}
          targetId={participantId}
        />
      }
      highlighted={isEditing}
      actions={
        <div className="flex items-center gap-3">
          {!bookingCancelled && (
            <EditModeToggle
              isEditing={isEditing}
              availability={exitEditing.availability}
              onToggle={exitEditing.toggleEditing}
            />
          )}
          {/* To samo menu co na ekranie rezerwacji, tylko z tym uczestnikiem
              zaznaczonym z góry w rezygnacji. W edycji go nie ma — rezygnacja
              wymaga rezerwacji bez otwartej paczki. */}
          {bookingView && !isEditing && (
            <BookingActionsMenu
              clientId={bookingView.clientId}
              onOpenWithdrawal={
                bookingCancelled || participantWithdrawn
                  ? undefined
                  : () => setIsWithdrawalOpen(true)
              }
            />
          )}
        </div>
      }
      banner={
        draft && (
          <ChangeDraftBanner
            draft={draft}
            isEditing={isEditing}
            scopeLabel={draftScopeLabel}
            editLock={exitEditing.availability}
            onResumeEditing={() => exitEditing.toggleEditing(true)}
            onWithdrawPackage={exitEditing.withdrawPackage}
            isWithdrawing={exitEditing.isDiscarding}
            onOpenFullPackage={openBookingInEditMode}
          />
        )
      }
    />
  );

  return (
    <DetailsProvider
      value={{
        bookingId,
        bookingCode: summaryQuery.data?.bookingCode ?? '',
        isEditing,
        onlyChanged,
        onOnlyChangedChange: setOnlyChanged,
        editField,
        currentPersonId: billParticipantId,
        onOpenBooking: openBooking,
      }}
    >
      <DetailsLayout
        header={header}
        sidebar={
          isTabMenu ? null : (
            <DetailsSidebar
              bookingId={bookingId}
              isEditing={isEditing}
              currentPersonId={billParticipantId}
              localDraftItemCount={view?.draftItemCount}
              onExitEditing={exitToView}
            />
          )
        }
        bottomBar={
          isEditing && draft && !isTabMenu ? (
            <TotalBar
              label="Wartość zmian"
              value={
                <MoneyAmountList values={draft.totalDelta} variant="signed" />
              }
            />
          ) : null
        }
      >
        <Outlet />

        {isWithdrawalOpen && bookingView && (
          <WithdrawalModal
            bookingId={bookingId}
            participants={bookingView.participants}
            initialParticipantId={billParticipantId}
            onClose={() => setIsWithdrawalOpen(false)}
          />
        )}

        {exitEditing.isExitModalOpen && draft && (
          <ExitEditingModal
            draft={draft}
            isDiscarding={exitEditing.isDiscarding}
            onChoice={exitEditing.resolveExit}
            onDismiss={exitEditing.dismissExit}
          />
        )}

        <EditFieldModalHost editField={editField} />
      </DetailsLayout>
    </DetailsProvider>
  );
};

export default ParticipantDetailsPage;
